import { ActionTree } from 'vuex';
import { AppState } from '@/store/app';
import { messagingWorkspacesApi } from '@/messaging/messaging';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { GET_CHANNEL_IDENTITIES } from './actionTypes';
import { ChannelIdentitiesState } from '@/store/channel-identities/index';
import { appsApi } from '@/features/apps/shared/services/apps';
import { SET_CHANNEL_IDENTITIES } from '@/store/channel-identities/mutationTypes';
import { ChannelIdentityViewModel } from '@/messaging/models/channelIdentityViewModel';
import { PublishedApp } from '@/features/apps/shared/models';

const APP_ID_MAPPING: {[id: string]: string} = {
	btsms: 'sms-bt',
	govuknotifysms: 'sms-govuknotify',
	twitter: 'twitter-dm',
	webassistant: 'web-assistant',
	messenger: 'messenger',
	connectbot: 'connectbot'
};

export const actions: ActionTree<ChannelIdentitiesState, AppState> = {
	async [GET_CHANNEL_IDENTITIES]({ commit, rootState, dispatch, state }, forceReload: boolean): Promise<ChannelIdentityViewModel[]> {
		const currentWorkspace = (rootState as any).app.currentWorkspace;
		if (!currentWorkspace) {
			return dispatch(HANDLE_SERIOUS_ERROR, 'No workspace selected');
		}

		if (!forceReload && state.channelIdentities.length !== 0) {
			return state.channelIdentities;
		}

		const identities = await messagingWorkspacesApi.getChannelIdentities(currentWorkspace.id);
		const publishedApps = await appsApi.listPublished(currentWorkspace.id);
		const installedChannelApps = publishedApps.filter(app => app.category === 'Customer Channel' && app.isInstalled);

		const results = getInstalledChannelIdentities(identities, installedChannelApps);
		results.push(...getInstalledChannelDefaultIdentities(identities, installedChannelApps));
		const orderedResults = sortChannelIdentitiesByTypeThenName(results, installedChannelApps);

		commit(SET_CHANNEL_IDENTITIES, orderedResults);
		return orderedResults;
	},
};

function getInstalledChannelIdentities(channelIdentities: ChannelIdentityViewModel[], installedChannelApps: PublishedApp[]): ChannelIdentityViewModel[] {
	const results: ChannelIdentityViewModel[] = [];
	for (const identity of channelIdentities) {
		const relatedApp = installedChannelApps.find(a => identity.type === (APP_ID_MAPPING[a.id] || a.id));
		if (relatedApp) {
			results.push(identity);
		}
	}
	return results;
}

function getInstalledChannelDefaultIdentities(channelIdentities: ChannelIdentityViewModel[], installedChannelApps: PublishedApp[]): ChannelIdentityViewModel[] {
	const results: ChannelIdentityViewModel[] = [];
	for (const app of installedChannelApps) {
		const type = APP_ID_MAPPING[app.id] || app.id;
		const doesChannelIdentityExistForAppType = channelIdentities.filter(ci => ci.type === type).length > 0;
		if (!doesChannelIdentityExistForAppType) {
			results.push({
				id: `${type}-default`,
				name: app.name,
				type,
				externalId: ''
			});
		}
	}
	return results;
}

function sortChannelIdentitiesByTypeThenName(channelIdentities: ChannelIdentityViewModel[], installedChannelApps: PublishedApp[]): ChannelIdentityViewModel[] {
	return channelIdentities.sort((c1, c2) => {
		const app1 = installedChannelApps.find(a => c1.type === (APP_ID_MAPPING[a.id] || a.id));
		const app2 = installedChannelApps.find(a => c2.type === (APP_ID_MAPPING[a.id] || a.id));
		const type1Name = app1 ? app1.name : c1.type;
		const type2Name = app2 ? app2.name : c2.type;

		if (type1Name === type2Name) {
			return c1.name.localeCompare(c2.name);
		}
		return type1Name.localeCompare(type2Name);
	});
}
